/**
 * Which browser extensions may receive a minted Markcat session.
 *
 * `/connect-extension` mints an access/refresh pair and messages it to whatever
 * extension id the URL names, so that id is an authorization decision rather
 * than a format question: any installed extension listing `markcat.app` in
 * `externally_connectable` would otherwise take credentials through a link the
 * user approved.
 *
 * `VITE_EXTENSION_IDS` (comma- or space-separated) is the allowlist and
 * *replaces* the default when set — list every id the build should trust.
 *
 * Three states, kept apart on purpose:
 *
 * - **Unset or blank** — the intentional development default: only the unpacked
 *   build is trusted. Its id is derived from the absolute path of the loaded
 *   folder, so `markcat-extension/dist` is stable. (`deploy/web.Dockerfile`
 *   declares the build ARG with an empty default, so a production build that
 *   never sets it lands here too — which is the current state: the Chrome Web
 *   Store id does not exist yet and cannot be defaulted here.)
 * - **Every entry a well-formed id** — exactly those ids are trusted.
 * - **Any entry malformed** — a configuration error, not a narrower allowlist.
 *   Nothing is trusted, so a typo in the store id cannot silently leave the
 *   production bundle handing sessions to the development identity.
 */

// Extension ids are 32 characters of a–p (base-16 over that alphabet).
const EXTENSION_ID_RE = /^[a-p]{32}$/;

/** Unpacked `markcat-extension/dist` — see `docs/reference/architecture.md`. */
export const DEVELOPMENT_EXTENSION_ID = 'oppdahmjgmffmlmipgpipecggbggooeo';

/** Split an allowlist env value into its entries, blank ones dropped. */
export function splitExtensionIds(raw: string | undefined): string[] {
  return (raw ?? '').split(/[\s,]+/).filter((entry) => entry.length > 0);
}

/**
 * The ids this build may hand a session to.
 *
 * Empty when `VITE_EXTENSION_IDS` is set to something malformed — an allowlist
 * that cannot be read is not a reason to fall back to a trusted identity.
 */
export function trustedExtensionIds(): string[] {
  const entries = splitExtensionIds(import.meta.env.VITE_EXTENSION_IDS);
  if (entries.length === 0) return [DEVELOPMENT_EXTENSION_ID];

  const malformed = entries.filter((entry) => !EXTENSION_ID_RE.test(entry));
  if (malformed.length > 0) {
    console.error(
      `VITE_EXTENSION_IDS is malformed (${malformed.join(', ')}); ` +
        'this build trusts no extension.',
    );
    return [];
  }
  return entries;
}

/** True only for an id this build is configured to hand a session to. */
export function isTrustedExtensionId(id: string | null | undefined): boolean {
  return !!id && trustedExtensionIds().includes(id);
}
